<template>
  <footer class="relative border-t border-orange-100 bg-[#FFFCF9]">
    <button
      v-show="showTop"
      class="scroll-progress fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-white text-ink shadow-[0_10px_30px_rgba(11,33,73,.16)] transition hover:-translate-y-0.5 hover:text-brand"
      type="button"
      aria-label="Scroll to top"
      @click="scrollToTop"
    >
      <svg class="pointer-events-none absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 48 48" aria-hidden="true">
        <circle class="scroll-progress__track" cx="24" cy="24" r="22" pathLength="100" />
        <circle class="scroll-progress__value" cx="24" cy="24" r="22" pathLength="100" :style="{ strokeDashoffset: 100 - scrollProgress }" />
      </svg>
      <i class="ri-arrow-up-line relative text-lg" aria-hidden="true"></i>
    </button>
    <div class="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 py-12 md:grid-cols-4 lg:px-8">
      <div>
        <div class="flex items-center gap-3">
          <img src="@/assets/logo.png" alt="Invi8 Logo" style="height:40px">
        </div>
        <p class="mt-4 max-w-xs text-sm leading-6 text-muted">Create invitations, manage guests, and discover venues and event professionals in one connected place.</p>
      </div>
      <div>
        <p class="text-sm font-semibold text-ink">Explore</p>
        <ul class="mt-4 space-y-2 text-sm text-muted">
          <li><RouterLink class="transition hover:text-ink" to="/vendors">Find Vendors</RouterLink></li>
          <li><RouterLink class="transition hover:text-ink" to="/for-users">For Users</RouterLink></li>
          <li><RouterLink class="transition hover:text-ink" to="/for-vendors">For Vendors</RouterLink></li>
          <li><RouterLink class="transition hover:text-ink" to="/use-cases">Use Cases</RouterLink></li>
          <li><RouterLink class="transition hover:text-ink" to="/faqs">FAQs</RouterLink></li>
          <li><RouterLink class="transition hover:text-ink" to="/about">About Invi8</RouterLink></li>
          <li><RouterLink class="transition hover:text-ink" to="/contact">Support</RouterLink></li>
        </ul>
      </div>
      <div>
        <p class="text-sm font-semibold text-ink">Legal</p>
        <ul class="mt-4 space-y-2 text-sm text-muted">
          <li><RouterLink class="transition hover:text-ink" to="/privacy-policy">Privacy Policy</RouterLink></li>
          <li><RouterLink class="transition hover:text-ink" to="/terms">Terms & Conditions</RouterLink></li>
        </ul>
      </div>
      <div>
        <p class="text-sm font-semibold text-ink">Planning an event?</p>
        <p class="mt-4 text-sm leading-6 text-muted">Create an invitation, organize your guests, and keep every update in one place.</p>
        <RouterLink class="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white shadow-card transition hover:bg-brandDark" to="/register">Get started <i class="ri-arrow-right-line"></i></RouterLink>
      </div>
    </div>
    <div class="border-t border-gray-100 bg-white py-6">
      <div class="mx-auto flex max-w-6xl flex-col justify-between gap-4 px-4 text-xs text-muted lg:flex-row lg:px-8 lg:items-center">
        <p>© {{ year }} Invi8. All rights reserved.</p>
        <div class="flex items-center gap-3">
          <RouterLink class="transition hover:text-ink" to="/privacy-policy">Privacy</RouterLink>
          <RouterLink class="transition hover:text-ink" to="/terms">Terms</RouterLink>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { RouterLink } from 'vue-router'
import { onMounted, onUnmounted, ref } from 'vue'

const year = new Date().getFullYear()
const showTop = ref(false)
const scrollProgress = ref(0)

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const handleScroll = () => {
  const y = window.scrollY || document.documentElement.scrollTop
  const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight
  scrollProgress.value = scrollableHeight > 0
    ? Math.min(100, Math.max(0, (y / scrollableHeight) * 100))
    : 0
  showTop.value = y > 160
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.scroll-progress {
  isolation: isolate;
}

.scroll-progress__track,
.scroll-progress__value {
  fill: none;
  stroke-width: 2.5;
}

.scroll-progress__track {
  stroke: #ffedd5;
}

.scroll-progress__value {
  stroke: #ff7a00;
  stroke-linecap: round;
  stroke-dasharray: 100;
  transition: stroke-dashoffset 180ms ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .scroll-progress,
  .scroll-progress__value {
    transition: none;
  }
}
</style>
